import { useEffect, useState, type ReactNode } from 'react'

/** How long each pace's leave takes, matched to the animation in `app.css`. */
const LEAVING: Record<Pace, number> = {
  brisk: 140,
  unhurried: 200,
}

/**
 * How quickly a panel arrives and leaves.
 *
 * `brisk` answers a press: the eye is already on the thing that opened, so the panel only has
 * to catch up with a hand that is still moving. `unhurried` arrives unbidden — a note appearing
 * in a corner nobody was looking at — and takes long enough that the movement itself is what
 * brings the eye over, rather than the note simply being there on the next blink.
 */
export type Pace = 'brisk' | 'unhurried'

/**
 * Every floating panel leaves the way it came: the holder keeps the panel mounted while the
 * leave animation plays, then lets it go. It has no box of its own, so the panel positions
 * against whatever the holder sits in.
 *
 * Without it a panel fades in and then disappears, which reads as two different things
 * happening — something arriving, and something being taken away between frames.
 */
export function FloatOut({
  open,
  pace = 'brisk',
  children,
}: {
  open: boolean
  pace?: Pace
  children: ReactNode
}) {
  const [held, setHeld] = useState(open)
  if (open && !held) {
    setHeld(true)
  }
  useEffect(() => {
    if (open || !held) {
      return
    }
    const timer = setTimeout(() => {
      setHeld(false)
    }, LEAVING[pace])
    return () => {
      clearTimeout(timer)
    }
  }, [open, held, pace])
  if (!open && !held) {
    return null
  }
  return (
    <span className="float-holder" data-float={open ? 'in' : 'out'} data-pace={pace}>
      {children}
    </span>
  )
}
